import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { DOG_BREEDS, COMMON_BREEDS, OTHER_BREED } from '../constants/breeds';
import { normalizeText } from '../utils/dataUtils';
import '../styles/components/breed-select.css';

/**
 * Selector de raza con búsqueda. Escribir filtra la lista; la raza se elige de
 * ahí, así que "Shih Tzu" se guarda siempre igual y no como Shitzu / Shihtzu /
 * Shih tzu.
 *
 * "Otro (especificar)" abre un campo libre para una raza que no esté en la
 * lista: el personal nunca se queda sin poder registrar un perro, y esos casos
 * se revisan después para agregarlos a constants/breeds.js.
 *
 * Al editar un perro cuya raza no está en la lista (los registros viejos traen
 * de todo) arranca en modo libre con ese valor, para no borrarlo sin querer.
 *
 * La lista se dibuja en un portal sobre el body, no dentro del formulario: las
 * tarjetas del formulario (.df-section) llevan overflow:hidden para redondear
 * sus esquinas, y ahí dentro la lista se cortaba al borde de la tarjeta. Por
 * eso se posiciona con coordenadas de pantalla, que se recalculan cuando algo
 * se desplaza o cambia de tamaño.
 */
const BreedSelect = ({ value = '', onChange, error = false, inputId = 'breed' }) => {
    const isKnown = !value || DOG_BREEDS.includes(value);

    const [custom, setCustom] = useState(!isKnown);
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [highlight, setHighlight] = useState(0);
    const [panelPos, setPanelPos] = useState(null);
    const wrapRef = useRef(null);
    const panelRef = useRef(null);
    const triggerRef = useRef(null);
    const searchRef = useRef(null);

    const PANEL_MAX = 290; // alto máximo del panel (buscador + lista)

    const reposition = useCallback(() => {
        const el = triggerRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const below = window.innerHeight - r.bottom;
        // Si abajo no cabe pero arriba sí, se abre hacia arriba.
        const flip = below < PANEL_MAX && r.top > below;
        setPanelPos({
            left: r.left,
            width: r.width,
            top: flip ? undefined : r.bottom + 4,
            bottom: flip ? window.innerHeight - r.top + 4 : undefined,
            maxHeight: Math.max(160, (flip ? r.top : below) - 12),
        });
    }, []);

    // Una raza libre que luego sí se agrega a la lista deja de ser "otro".
    useEffect(() => {
        if (value && DOG_BREEDS.includes(value)) setCustom(false);
    }, [value]);

    // Cerrar al hacer clic fuera. El panel vive fuera del formulario, así que
    // un clic dentro de él no cuenta como "fuera".
    useEffect(() => {
        if (!open) return;
        const onClickOutside = (e) => {
            if (wrapRef.current?.contains(e.target)) return;
            if (panelRef.current?.contains(e.target)) return;
            setOpen(false);
        };
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, [open]);

    // El formulario se desplaza dentro de su propio contenedor, no en la
    // ventana: con capture se escuchan también esos desplazamientos y el panel
    // sigue al campo en lugar de quedarse flotando.
    useEffect(() => {
        if (!open) return;
        reposition();
        window.addEventListener('scroll', reposition, true);
        window.addEventListener('resize', reposition);
        return () => {
            window.removeEventListener('scroll', reposition, true);
            window.removeEventListener('resize', reposition);
        };
    }, [open, reposition]);

    // El buscador se enfoca al montarse, no al abrirse: el panel vive en un
    // portal y aún no existe en el DOM cuando `open` cambia.
    const focusSearch = useCallback((el) => {
        searchRef.current = el;
        if (el) el.focus();
    }, []);

    const matches = useMemo(() => {
        const term = normalizeText(search.trim());
        if (!term) return [...COMMON_BREEDS, ...DOG_BREEDS.filter(b => !COMMON_BREEDS.includes(b))];
        // Las que empiezan con lo escrito primero: teclear "pas" debe ofrecer
        // Pastor Alemán antes que Antiguo Pastor Inglés.
        const starts = [];
        const contains = [];
        for (const breed of DOG_BREEDS) {
            const n = normalizeText(breed);
            if (n.startsWith(term)) starts.push(breed);
            else if (n.includes(term)) contains.push(breed);
        }
        return [...starts, ...contains];
    }, [search]);

    useEffect(() => setHighlight(0), [search]);

    const pick = (breed) => {
        if (breed === OTHER_BREED) {
            setCustom(true);
            setOpen(false);
            setSearch('');
            onChange('');
            return;
        }
        onChange(breed);
        setOpen(false);
        setSearch('');
    };

    const handleKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlight(h => Math.min(h + 1, matches.length));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlight(h => Math.max(h - 1, 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (highlight === matches.length) pick(OTHER_BREED);
            else if (matches[highlight]) pick(matches[highlight]);
        } else if (e.key === 'Escape') {
            setOpen(false);
        }
    };

    if (custom) {
        return (
            <div className="bs-wrap">
                <input
                    id={inputId}
                    className={`df-input ${error ? 'df-input--err' : ''}`}
                    type="text"
                    value={value}
                    onChange={e => onChange(e.target.value)}
                    placeholder="Escribe la raza"
                    autoComplete="off"
                    autoFocus
                />
                <button
                    type="button"
                    className="bs-back"
                    onClick={() => { setCustom(false); onChange(''); setOpen(true); }}
                >
                    ← Elegir de la lista
                </button>
            </div>
        );
    }

    return (
        <div className="bs-wrap" ref={wrapRef}>
            <button
                type="button"
                id={inputId}
                ref={triggerRef}
                className={`bs-trigger ${error ? 'bs-trigger--err' : ''}`}
                onClick={() => setOpen(o => !o)}
                aria-haspopup="listbox"
                aria-expanded={open}
            >
                <span className={value ? 'bs-value' : 'bs-placeholder'}>
                    {value || 'Selecciona la raza'}
                </span>
                <span className="bs-caret">▾</span>
            </button>

            {open && panelPos && createPortal(
                <div
                    className="bs-panel"
                    ref={panelRef}
                    style={{
                        left: panelPos.left,
                        width: panelPos.width,
                        top: panelPos.top,
                        bottom: panelPos.bottom,
                    }}
                >
                    <input
                        ref={focusSearch}
                        className="bs-search"
                        type="text"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Buscar raza…"
                        autoComplete="off"
                    />
                    <div
                        className="bs-list"
                        role="listbox"
                        style={{ maxHeight: Math.max(120, panelPos.maxHeight - 46) }}
                    >
                        {matches.map((breed, i) => (
                            <button
                                key={breed}
                                type="button"
                                role="option"
                                aria-selected={breed === value}
                                className={`bs-option${i === highlight ? ' bs-option--on' : ''}${breed === value ? ' bs-option--picked' : ''}`}
                                onMouseEnter={() => setHighlight(i)}
                                onClick={() => pick(breed)}
                            >
                                {breed}
                            </button>
                        ))}
                        {matches.length === 0 && (
                            <p className="bs-empty">Ninguna raza coincide con «{search}».</p>
                        )}
                        <button
                            type="button"
                            role="option"
                            aria-selected={false}
                            className={`bs-option bs-option--other${highlight === matches.length ? ' bs-option--on' : ''}`}
                            onMouseEnter={() => setHighlight(matches.length)}
                            onClick={() => pick(OTHER_BREED)}
                        >
                            ✏️ Otro (especificar)
                        </button>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default BreedSelect;
